import logoText from '@assets/logos/mrwolf-black-completo-160@2x.png';
import WolfButton from '@components/ui/WolfButton';
import WolfCheckbox from '@components/ui/WolfCheckbox';
import WolfInput from '@components/ui/WolfInput';
import WolfTextLink from '@components/ui/WolfTextLink';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { validEmail } from '@utils/validEmail';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

export interface SignupFormProps {
  onSubmit: (
    organizationName: string,
    organizationDescription: string,
    ownerEmail: string,
    ownerFirstName: string,
    ownerLastName: string,
  ) => void;
  errorMessage?: string;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSubmit, errorMessage }) => {
  const history = useHistory();
  const [organizationName, setOrganizationName] = useState('');
  const [organizationDescription, setOrganizationDescription] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);

  const localeLogin = useTranslation('login');
  const localeCommon = useTranslation('common');
  const localeUsers = useTranslation('users');
  const localeActionMessages = useTranslation('actionMessages');

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (value: string) => void) => {
    const value = e.target.value;
    setter(value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmedValue = e.target.value.trim();
    setEmail(trimmedValue);
    e.target.value = trimmedValue;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationName || !email || !firstName || !lastName) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['invalidOrganizationData'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    if (!validEmail(email)) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['invalidEmail'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }

    onSubmit(organizationName, organizationDescription, email, firstName, lastName);
  };

  const goToLogin = () => {
    history.push('/login');
  };

  const goToLanding = () => {
    window.location.href = process.env.REACT_APP_MARKETING_URL || '/';
  };

  return (
    <Box>
      <div onClick={goToLanding} style={{ cursor: 'pointer' }}>
        <img src={logoText} alt="logo" width="110" height="32" className="absolute top-5 left-5" />
        <WolfTextLink size="24px" align="right" className="absolute top-5 right-5" onClick={goToLanding}>
          {localeCommon['back']}
        </WolfTextLink>
      </div>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          maxWidth: isSmallScreen ? 300 : 400,
          minWidth: isSmallScreen ? 300 : 400,
          width: isSmallScreen ? 300 : 400,
        }}>
        <Typography variant="h1" sx={{ mb: 2, textAlign: 'center' }}>
          {localeLogin['createYourOrganization']}
        </Typography>
        <Typography variant="body14" sx={{ mb: 6, textAlign: 'center', color: '#666' }}>
          {localeLogin['signupDescription']}
        </Typography>

        <form onSubmit={handleSubmit} className="w-full flex flex-col items-center">
          <WolfInput
            placeholder={localeCommon['organization'] + ' ' + localeCommon['name']}
            label={localeCommon['organization'] + ' ' + localeCommon['name']}
            variant="outlined"
            required
            fullWidth
            className="!mb-4"
            autoComplete="organization"
            value={organizationName}
            onChange={(e) => handleInputChange(e, setOrganizationName)}
          />

          <WolfInput
            placeholder={localeCommon['organization'] + ' ' + localeCommon['description']}
            label={localeCommon['organization'] + ' ' + localeCommon['description']}
            variant="outlined"
            fullWidth
            className="!mb-6"
            autoComplete="off"
            value={organizationDescription}
            onChange={(e) => handleInputChange(e, setOrganizationDescription)}
          />

          <Typography variant="body14" sx={{ mb: 3, textAlign: 'center', color: '#888', fontSize: '12px' }}>
            {localeLogin['accountOwnerInformation']}
          </Typography>

          <WolfInput
            placeholder={localeUsers['firstName']}
            label={localeUsers['firstName']}
            variant="outlined"
            required
            fullWidth
            className="!mb-4"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => handleInputChange(e, setFirstName)}
          />

          <WolfInput
            placeholder={localeUsers['lastName']}
            label={localeUsers['lastName']}
            variant="outlined"
            required
            fullWidth
            className="!mb-4"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => handleInputChange(e, setLastName)}
          />

          <WolfInput
            placeholder={localeLogin['enterEmail']}
            label={localeLogin['email']}
            variant="outlined"
            required
            fullWidth
            tooltipText={localeLogin['emailTooltip']}
            className="!mb-4"
            autoComplete="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
          />

          <Box className="w-full !mb-4 flex items-start">
            <WolfCheckbox
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                if (e.target.checked) setTermsError(false);
              }}
            />
            <Box sx={{ fontSize: '14px', lineHeight: '1.4', ml: 1 }}>
              <Typography variant="body14" component="span" sx={{ fontSize: '14px' }}>
                {localeLogin['agreeToTerms']}{' '}
              </Typography>
              <WolfTextLink
                onClick={() => window.open(process.env.REACT_APP_MARKETING_URL + '/terms', '_blank')}
                style={{ fontSize: '14px', display: 'inline' }}>
                {localeLogin['termsAndConditions']}
              </WolfTextLink>
              <Typography variant="body14" component="span" sx={{ fontSize: '14px' }}>
                {' '}
                {localeLogin['and']}{' '}
              </Typography>
              <WolfTextLink
                onClick={() => window.open(process.env.REACT_APP_MARKETING_URL + '/privacy', '_blank')}
                style={{ fontSize: '14px', display: 'inline' }}>
                {localeLogin['privacyPolicy']}
              </WolfTextLink>
            </Box>
          </Box>
          {termsError && (
            <Typography color="error" variant="body14" sx={{ mb: 2, width: '100%' }}>
              {localeLogin['mustAcceptTerms']}
            </Typography>
          )}

          <WolfButton type="submit" color="primary" fullWidth className="!my-4">
            {localeLogin['createAccount']}
          </WolfButton>

          {errorMessage && (
            <Typography color="error" variant="body14" sx={{ mb: 2, textAlign: 'center' }}>
              {errorMessage}
            </Typography>
          )}

          <Box className="flex flex-col items-center gap-2 !mt-4">
            <Typography variant="body14">{localeLogin['alreadyHaveAccount']}</Typography>
            <WolfTextLink onClick={goToLogin} align="center">
              {localeLogin['signIn']}
            </WolfTextLink>
          </Box>

          <WolfTextLink
            size={isSmallScreen ? '14px' : '16px'}
            onClick={() => window.open(process.env.REACT_APP_MARKETING_URL + '/health', '_blank')}
            align="center"
            className="!mt-4">
            {localeLogin['serviceStatus']}
          </WolfTextLink>
        </form>
      </Box>
    </Box>
  );
};

export default SignupForm;
